/**
 * Remix right surface block control, registered via the UI Control Registry API as a replacement
 * for `RemixButtonDecoration`. Positioning is fully owned by the block-controls right-surface host
 * (`BlockControlsRightSurfaces` / `getSurfacePlacement`) — this component only renders content.
 */
import React, { useCallback, useRef } from 'react';

import { useIntl } from 'react-intl';

import IconButton from '@atlaskit/button/icon/button';
import { BLOCK_CONTROL_UI_CONTEXT } from '@atlaskit/editor-common/block-controls/block-control-ui-context';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/useSharedPluginStateWithSelector';
import type { CommonComponentProps } from '@atlaskit/editor-ui-control-model/types';
import AiGenerativeRemixIcon from '@atlaskit/icon-lab/core/ai-generative-remix';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { startRemixCreateExperience } from '@atlassian/conversation-assistant-instrumentation/controllers/experience-tracker/remix-create-collect';
import { getRemixFeatureArea } from '@atlassian/conversation-assistant-instrumentation/controllers/experience-tracker/remix-feature-area';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';

import type { MalleableUiPlugin, RemixDisabledReason } from '../../malleableUiPluginType';
import { remixHoverHighlightPluginKey } from '../../pm-plugins/remix-hover-highlight-plugin';
import { messages } from '../messages';
import { getRemixDisabledTooltip } from '../remix-disabled-tooltip';
import { hasAiEditInProgressState } from '../utils/is-ai-edit-in-progress';

export type RemixRightSurfaceButtonProps = CommonComponentProps & {
	api?: ExtractInjectionAPI<MalleableUiPlugin>;
	isDisabled?: boolean;
	isRovoLLMEnabled?: boolean;
	livePage?: boolean;
	remixDisabledReason?: RemixDisabledReason;
};

export const RemixRightSurfaceButton = ({
	api,
	isDisabled,
	isRovoLLMEnabled,
	livePage,
	remixDisabledReason,
	surfaceContext,
}: RemixRightSurfaceButtonProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const context = surfaceContext?.get(BLOCK_CONTROL_UI_CONTEXT);
	const rootNodeType = context?.rootNode.type.name ?? context?.targetNode.type.name ?? '';
	const rootPos = context?.rootNode.pos ?? context?.targetNode.pos;

	const {
		editorViewMode,
		contentId,
		isOwnRemixing,
		isAnyRemixing,
		aiEditInProgress,
		areAiEntryPointsDisabled,
	} = useSharedPluginStateWithSelector(
		api,
		['editorViewMode', 'contextIdentifier', 'malleableUi', 'aiExperience'],
		(states) => ({
			editorViewMode: states.editorViewModeState?.mode,
			contentId: states.contextIdentifierState?.contextIdentifierProvider?.objectId,
			isOwnRemixing: Boolean(
				states.malleableUiState?.isRemixModalOpen || states.malleableUiState?.isThinking,
			),
			// Only the editor treatment projects this, from the shared Remix registry.
			isAnyRemixing: states.malleableUiState?.isAnyRemixing ?? false,
			areAiEntryPointsDisabled: states.aiExperienceState?.areAiEntryPointsDisabled ?? false,
			aiEditInProgress: hasAiEditInProgressState(states.aiExperienceState),
		}),
	);
	// A Remix from another surface (e.g. the Object Sidebar, including one still generating in
	// Rovo Chat after its handoff) is also in progress, and the machine would refuse a new start.
	const isRemixing = isOwnRemixing || isAnyRemixing;

	// pageMode for analytics: 'editor' = full page editor, 'liveEdit' = live page edit, 'liveView' = live page view (livePage set by Confluence).
	const pageMode =
		editorViewMode === 'edit' && !livePage
			? 'editor'
			: editorViewMode === 'edit' && livePage
				? 'liveEdit'
				: editorViewMode === 'view' && livePage
					? 'liveView'
					: undefined;

	const isRemixAvailable =
		isRovoLLMEnabled && expValEqualsNoExposure('cc-maui-experiment', 'isEnabled', true);
	const hasRemixOrEditInProgress = isRemixing || aiEditInProgress;

	const nodePosRef = useRef<number | undefined>(rootPos);
	nodePosRef.current = rootPos;
	const experienceTracker = useAIMateExperienceTracker();

	const clearHoverHighlight = useCallback(() => {
		if (fg('cc-maui-phase-3')) {
			api?.core.actions.execute(({ tr }) =>
				tr.setMeta(remixHoverHighlightPluginKey, { highlightPos: null }),
			);
		}
	}, [api]);

	const openRemix = useCallback(() => {
		const featureArea = getRemixFeatureArea({});
		experienceTracker.start({
			name: ExperienceName.REMIX_START,
			id: ExperienceName.REMIX_START,
			attributes: {
				entryPoint: 'editor',
				remixSource: 'blockNudge',
				product: 'confluence',
				...(featureArea !== undefined && { featureArea }),
			},
		});
		startRemixCreateExperience({
			experienceTracker,
			attributes: { entryPoint: 'editor', remixSource: 'blockNudge', product: 'confluence' },
		});
		// Clear the hover highlight decoration before opening the modal to prevent flashing
		clearHoverHighlight();
		api?.core.actions.execute(
			api?.malleableUi.commands.openRemixModal({
				nodePos: nodePosRef.current,
				nodeType: rootNodeType,
				metadata: {
					interactionSource: 'remixModalFromNudge',
				},
				source: 'blockNudge',
			}),
		);
	}, [api, clearHoverHighlight, experienceTracker, rootNodeType]);

	const handleClick = useCallback(() => {
		if (isDisabled) {
			return;
		}

		openRemix();
	}, [isDisabled, openRemix]);

	const handleRemixButtonMouseEnter = useCallback(() => {
		if (!fg('cc-maui-phase-3') || rootPos === undefined) {
			return;
		}
		api?.core.actions.execute(({ tr }) =>
			tr.setMeta(remixHoverHighlightPluginKey, { highlightPos: rootPos, rootNodeType }),
		);
	}, [api, rootNodeType, rootPos]);

	const handleRemixButtonMouseLeave = useCallback(() => {
		if (!fg('cc-maui-phase-3')) {
			return;
		}
		api?.core.actions.execute(({ tr }) =>
			tr.setMeta(remixHoverHighlightPluginKey, { highlightPos: null }),
		);
	}, [api]);

	if (!isRemixAvailable || areAiEntryPointsDisabled || hasRemixOrEditInProgress) {
		return null;
	}

	const tooltipContent = isRemixing
		? formatMessage(messages.remixInProgressTooltip)
		: isDisabled
			? getRemixDisabledTooltip(formatMessage, remixDisabledReason)
			: fg('cc_maui_updated_remix_tooltip_text')
				? formatMessage(messages.remixBlockTooltipUpdated)
				: formatMessage(messages.remixBlockTooltip);

	const renderIconButton = () => (
		<Tooltip content={tooltipContent} position="top">
			<IconButton
				spacing="compact"
				appearance="subtle"
				shape="circle"
				label={formatMessage(messages.remixBlockLabel)}
				icon={() => <AiGenerativeRemixIcon label="" size="small" />}
				isDisabled={isDisabled || isRemixing}
				analyticsContext={{
					actionSubjectId: 'remix',
					source: 'remixModalFromNudge',
					attributes: {
						...(pageMode !== undefined && { pageMode }),
						nodeType: rootNodeType,
						...(contentId !== undefined && contentId !== '' && { contentId }),
					},
				}}
				testId="visible-block-ctrl-remix-button"
				onMouseEnter={handleRemixButtonMouseEnter}
				onMouseLeave={handleRemixButtonMouseLeave}
				onBlur={handleRemixButtonMouseLeave}
				onMouseDown={(e) => e.preventDefault()}
				onClick={handleClick}
			/>
		</Tooltip>
	);

	return renderIconButton();
};
